// Roster and attendance (docs/plans/phase3-operations-modules.md §4.2). URLs carry IDs,
// dates, the unit type and the ward ID or department name (staff data, not patient data).
import { queryOptions } from '@tanstack/react-query';
import {
  attendanceReportSchema,
  myAttendanceSchema,
  rosterCandidatesSchema,
  rosterSchema,
  rosterShiftListSchema,
  rosterSwapListSchema,
  rosterUnitsSchema,
  type AddRosterEntriesRequest,
  type RequestSwapRequest,
  type SaveShiftsRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export interface UnitRange {
  unitType: 'ward' | 'department';
  unit: string;
  from: string;
  to: string;
}

const rangeQuery = (r: UnitRange) =>
  `unitType=${r.unitType}&unit=${encodeURIComponent(r.unit)}&from=${r.from}&to=${r.to}`;

export const rosterKeys = {
  all: ['roster'] as const,
  shifts: ['roster', 'shifts'] as const,
  units: ['roster', 'units'] as const,
  roster: (r: UnitRange) => ['roster', 'grid', r.unitType, r.unit, r.from, r.to] as const,
  candidates: (department: string) => ['roster', 'candidates', department] as const,
  swaps: ['roster', 'swaps'] as const,
  me: ['attendance', 'me'] as const,
  report: (r: UnitRange) => ['attendance', 'report', r.unitType, r.unit, r.from, r.to] as const,
};

export const shiftsQuery = queryOptions({
  queryKey: rosterKeys.shifts,
  queryFn: () => api('GET', '/roster/shifts', { schema: rosterShiftListSchema }),
});

export const unitsQuery = queryOptions({
  queryKey: rosterKeys.units,
  queryFn: () => api('GET', '/roster/units', { schema: rosterUnitsSchema }),
});

export function rosterQuery(r: UnitRange) {
  return queryOptions({
    queryKey: rosterKeys.roster(r),
    queryFn: () => api('GET', `/roster?${rangeQuery(r)}`, { schema: rosterSchema }),
  });
}

export function candidatesQuery(department: string) {
  return queryOptions({
    queryKey: rosterKeys.candidates(department),
    queryFn: () =>
      api('GET', `/roster/candidates?department=${encodeURIComponent(department)}`, {
        schema: rosterCandidatesSchema,
      }),
  });
}

export const swapsQuery = queryOptions({
  queryKey: rosterKeys.swaps,
  queryFn: () => api('GET', '/roster/swaps?status=requested', { schema: rosterSwapListSchema }),
});

export const myAttendanceQuery = queryOptions({
  queryKey: rosterKeys.me,
  queryFn: () => api('GET', '/attendance/me', { schema: myAttendanceSchema }),
});

export function attendanceReportQuery(r: UnitRange) {
  return queryOptions({
    queryKey: rosterKeys.report(r),
    queryFn: () =>
      api('GET', `/attendance/report?${rangeQuery(r)}`, { schema: attendanceReportSchema }),
  });
}

export const saveShifts = (body: SaveShiftsRequest) =>
  api('PUT', '/roster/shifts', { body, schema: rosterShiftListSchema });
export const addRosterEntries = (body: AddRosterEntriesRequest) =>
  api('POST', '/roster/entries', { body, schema: z.object({ added: z.number() }) });
export const removeRosterEntry = (id: string, reason: string) =>
  api('POST', `/roster/entries/${id}/remove`, { body: { reason } });
export const requestSwap = (body: RequestSwapRequest) =>
  api('POST', '/roster/swaps', { body, schema: z.object({ id: z.uuid() }) });
export const decideSwap = (id: string, approve: boolean, reason: string | null) =>
  api('POST', `/roster/swaps/${id}/${approve ? 'approve' : 'reject'}`, { body: { reason } });
export const clockIn = () => api('POST', '/attendance/clock-in', { schema: myAttendanceSchema });
export const clockOut = () => api('POST', '/attendance/clock-out', { schema: myAttendanceSchema });
